'use client';

import Link from "next/link"
import { useRouter } from 'next/navigation';
import { useRequest } from 'ahooks';
import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import api from "@/utils/api";
import { setCurrentUser, getCurrentUser } from "@/utils/session";
import { useToast } from "@/hooks/use-toast"
import { Loader2 } from "lucide-react"

interface LoginResponse {
  user_id: string;
  username: string;
  nickname: string;
  access_token: string;
  refresh_token: string;
  organizations: string[];
}

export function LoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const { toast } = useToast();

  const { loading, run } = useRequest(async (values) => {
    try {
      const form = new FormData();
      form.append('username', values.email);
      form.append('password', values.password);
      
      const response_data = await api.post('/api/user/login', form);
      const data = response_data as unknown as LoginResponse;
      
      setCurrentUser({
        userid: data.user_id,
        username: data.username,
        nickname: data.nickname,
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        organizations: data.organizations,
      });
      
      toast({
        title: "Login Successful",
        description: "Welcome back!",
        variant: "default",
      });
      
      setIsNavigating(true);
      await router.push('/platform/tasks');
    } catch (error) {
      console.error('Login error:', error);
      setIsNavigating(false);
      
      let errorMessage = 'Login failed, please try again later';
      
      if (error && typeof error === 'object') {
        if ('response' in error && 
            error.response && 
            typeof error.response === 'object') {
          if ('data' in error.response && 
              error.response.data && 
              typeof error.response.data === 'object' && 
              'message' in error.response.data) {
            errorMessage = String(error.response.data.message);
          } else if ('statusText' in error.response) {
            errorMessage = `Login failed: ${error.response.statusText}`;
          }
        } else if ('message' in error) {
          errorMessage = String(error.message);
        }
      }
      
      toast({
        title: "Login Failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      throw error;
    }
  }, { manual: true });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = getCurrentUser();
        if (currentUser?.access_token) {
          try {
            await api.get('/api/user/verify');
            router.push('/platform/tasks');
            return;
          } catch {
            console.log('Token invalid, requiring login');
          }
        }
      } catch {
        console.log('No stored credentials found');
      }
      setIsChecking(false);
    };

    checkAuth();
  }, [router]);

  const isDisabled = loading || isNavigating;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    run({ email, password });
  };

  if (isChecking) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card className="mx-auto max-w-sm">
      <CardHeader>
        <CardTitle className="text-2xl">Login</CardTitle>
        <CardDescription>
          Enter your email below to login to your account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="m@example.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isDisabled}
            />
          </div>
          <div className="grid gap-2">
            <div className="flex items-center">
              <Label htmlFor="password">Password</Label>
              {/* <Link 
                href="#" 
                className={`ml-auto inline-block text-sm underline ${
                  isDisabled ? 'pointer-events-none opacity-50' : ''
                }`}
              >
                Forgot your password?
              </Link> */}
            </div>
            <Input 
              id="password" 
              type="password" 
              required 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isDisabled}
            />
          </div>
          <div></div>
          <Button 
            type="submit" 
            className="w-full bg-amber-500 hover:bg-amber-600"
            disabled={isDisabled}
          >
            {isDisabled ? 'Logging...' : 'Login'}
          </Button>
        </form>
        <div className="p-6 pt-0">
          <div className="text-center text-sm">
            Don&apos;t have an account?{" "}
            <Link 
              href="/register" 
              className="text-amber-600 hover:text-amber-700 font-medium hover:underline"
            >
              Register now
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
